import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FormInput, FormSelect, FormUpload, Button, EligCheck, OtherDocsUpload } from 'components';
import useConfigOptions from 'hooks/useConfigOptions';
import { preCheckLoanEligibility } from '../../misc/loanPreChecks';
import { getDateAfterTimeframe, nextMonthStartDate } from 'components/misc/functions';
import { useDispatch } from 'react-redux';
import { show } from 'providers/toastSlice';
import LoanRequirement from './LoanRequirement';
import ReviewModal from 'components/Utils/ReviewModal';
import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01Icon } from '@hugeicons/core-free-icons';

function SectionCard({ number, title, children }) {
    return (
        <div className="col-span-full">
            <div className="rounded-xl border border-gray-200 dark:border-white/[.08] bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
                <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100 dark:border-white/[.06] bg-gray-50/50 dark:bg-white/[.02]">
                    <span className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center text-xs font-bold text-amber-700 dark:text-amber-400 shrink-0">
                        {number}
                    </span>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</h3>
                </div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {children}
                </div>
            </div>
        </div>
    );
}

function Pension({ client }) {
    const dispatch = useDispatch();
    const {
        // pre-existing
        sectorOpts, billPaymentOptions, insuranceOpts, purposeOfLoanOpts, pensionCompanies, loanAmtLimits,
        // newly migrated off the static `components/misc/constants` import
        optionsYN, numOfYearsWithEmployer, missedPaymentOptions, billsHistoryOpts,
        paymentPlanOpts, paymentDurationOpts, incomeFrequencyOpts, automaticPaymentOpts,
        pensionStateOpts, pensionContYrsOpts, pensionContOneYrOpts, avgPensionContOpts,
        pensionCollateralOpts, relationshipManagers,
    } = useConfigOptions();

    const fullName = client?.otherNames + ' ' + client?.surname;

    const [isOpen, setIsOpen] = useState(false);
    const [isLoanReqOpen, setIsLoanReqOpen] = useState(false);
    const [reviewModalOpen, setReviewModalOpen] = useState(false);

    const closeModal = () => setIsOpen(false);
    const openModal = () => setIsOpen(true);

    const [employedCurrently, setEmployedCurrently] = useState('');
    const [salaryStatus, setSalaryStatus] = useState('');
    const [incomeStatus, setIncomeStatus] = useState('');
    const [incomeAmt, setIncomeAmt] = useState('');
    const [debtStatus, setDebtStatus] = useState('');
    const [debtAmt, setDebtAmt] = useState(0);
    const [automatedPaymentsStatus, setAutomatedPaymentsStatus] = useState('');
    const [pensionID, setPensionID] = useState('');
    const [pensionAsset, setPensionAsset] = useState('');
    const [pensionContYrs, setPensionContYrs] = useState('');
    const [pensionContOneYr, setPensionContOneYr] = useState('');
    const [avgPensionCont, setAvgPensionCont] = useState('');
    const [collateralType, setCollateralType] = useState('');
    const [employerName, setEmployerName] = useState('');
    const [yearsAtWork, setYearsAtWork] = useState('');
    const [employmentSector, setEmploymentSector] = useState('');
    const [outstandingLoans, setOutstandingLoans] = useState('');
    const [loansTaken, setLoansTaken] = useState('');
    const [custRelTime, setCustRelTime] = useState('');
    const [loanDefaultStatus, setLoanDefaultStatus] = useState('');
    const [defaultedLoanStatus, setDefaultedLoanStatus] = useState('');
    const [billsOption, setBillsOption] = useState('');
    const [billsHistory, setBillsHistory] = useState('');
    const [billsHistoryStatus, setBillsHistoryStatus] = useState('');
    const [loanPaymentHistory, setLoanPaymentHistory] = useState('');
    const [billPaymentHistory, setBillPaymentHistory] = useState('');
    const [savingHabits, setSavingHabits] = useState('');
    const [savingsFreq1, setSavingsFreq1] = useState('');
    const [savingsFreq2, setSavingsFreq2] = useState('');
    const [savingsFreq3, setSavingsFreq3] = useState('');
    const [typeOfInsurance, setTypeOfInsurance] = useState('');
    const [eligibleSavingsHabit, setEligibleSavingsHabit] = useState('');
    const [amountReq, setAmountReq] = useState('');
    const [purpose, setPurpose] = useState('');
    const [paymentPlan, setPaymentPlan] = useState('');
    const [paymentDuration, setPaymentDuration] = useState('');
    const [repaySource, setRepaySource] = useState('');
    const [freqIncome, setFreqIncome] = useState('');
    const [autoDeduction, setAutoDeduction] = useState('');
    const [pensionState, setPensionState] = useState('');
    const [tier_3, setTier_3] = useState('');
    const [incomeProof, setIncomeProof] = useState('');
    const [residenceProof, setResidenceProof] = useState('');
    const [bankOrMomoStatement, setBankOrMomoStatement] = useState('');
    const [otherDocs, setOtherDocs] = useState([]);
    const [startDate, setStartDate] = useState(getDateAfterTimeframe('daily'));
    const [relManager, setRelManager] = useState('');
    const [guarantorFirstName, setGuarantorFirstName] = useState('');
    const [guarantorLastName, setGuarantorLastName] = useState('');
    const [guarantorPhone, setGuarantorPhone] = useState('');
    const [guarantorAddress, setGuarantorAddress] = useState('');
    const [guarantorRelationship, setGuarantorRelationship] = useState('');
    const guarantorIdType = 'GhanaCard';
    const [guarantorIdNumber, setGuarantorIdNumber] = useState('');
    const [guarantorDigitalAddress, setGuarantorDigitalAddress] = useState('');
    const [guarantorDob, setGuarantorDob] = useState('');
    const signedName = fullName;
    const [processing, setProcessing] = useState(false);
    const [preCheckData, setPreCheckData] = useState({});

    const handleRequest = async (e) => {
        e.preventDefault();
        setProcessing(true);

        if (amountReq > loanAmtLimits.pensionLoanLimit) {
            dispatch(show({ state: true, message: `Loan amount requested exceeds the limit of GHS${loanAmtLimits.pensionLoanLimit}`, type: 'error' }));
            setProcessing(false);
            return;
        }

        if (!incomeProof) {
            setProcessing(false);
            dispatch(show({ type: 'error', state: true, message: 'Must attach proof of income' }));
            return;
        }
        if (!residenceProof) {
            setProcessing(false);
            dispatch(show({ type: 'error', state: true, message: 'Must attach proof of residence' }));
            return;
        }
        if (!bankOrMomoStatement) {
            setProcessing(false);
            dispatch(show({ type: 'error', state: true, message: 'Must attach bank or momo statement' }));
            return;
        }

        let data = { employedCurrently, salaryStatus, incomeStatus, incomeAmt, debtAmt, automatedPaymentsStatus, pensionID, pensionAsset, pensionContYrs, pensionContOneYr, avgPensionCont, collateralType, employerName, yearsAtWork, employmentSector, outstandingLoans, loansTaken, custRelTime, loanDefaultStatus, defaultedLoanStatus, billsOption, billsHistory, billsHistoryStatus, loanPaymentHistory, billPaymentHistory, savingHabits, savingsFreq1, savingsFreq2, savingsFreq3, typeOfInsurance, eligibleSavingsHabit, amountReq, purpose, paymentPlan, paymentDuration, repaySource, freqIncome, autoDeduction, pensionState, tier_3, incomeProof, residenceProof, bankOrMomoStatement, otherDocs, startDate, relManager, guarantorFirstName, guarantorLastName, guarantorPhone, guarantorAddress, guarantorRelationship, guarantorIdType, guarantorIdNumber, guarantorDigitalAddress, guarantorDob, signedName };

        let response = await preCheckLoanEligibility('pension', data);
        setPreCheckData({ ...response, data });

        setTimeout(() => {
            setProcessing(false);
            openModal();
        }, 3000);
    };

    useEffect(() => {
        setIsLoanReqOpen(true);
    }, []);

    useEffect(() => {
        if (debtStatus === 'No') setDebtAmt(0);
        if (debtStatus === 'Yes') setDebtAmt('');
    }, [debtStatus]);

    return (
        <>
        <LoanRequirement
            isOpen={isLoanReqOpen}
            setIsLoanReqOpen={setIsLoanReqOpen}
            type="Pension Backed"
            requirements={[
                'Proof of Income (Payslip)',
                'Proof of Residence (eg: Utility Bill or Tenancy Agreement)',
                '3 Months Bank or Mobile Money Statement',
            ]}
        />

        <div className="display_card">
            <form className="grid grid-cols-1 gap-5" encType="multipart/form-data" onSubmit={handleRequest}>

                {/* 1 — Income Information */}
                <SectionCard number="1" title="Income Information">
                    <FormSelect label="Are you employed with your current employer for at least 1 year?" name="employedCurrently" id="employedCurrently" content={optionsYN} value={employedCurrently} setValue={setEmployedCurrently} required={true} />
                    <FormSelect label="Is your regular monthly income equal to or greater than GHS2,000 per month?" name="salaryStatus" id="salaryStatus" content={optionsYN} value={salaryStatus} setValue={setSalaryStatus} required={true} />
                    <FormSelect label="Do you receive regular monthly income (Employment or other income)?" name="incomeStatus" id="incomeStatus" content={optionsYN} value={incomeStatus} setValue={setIncomeStatus} required={true} />
                    <FormInput label="Provide your monthly regular income" name="incomeAmt" id="incomeAmt" type="number" value={incomeAmt} setValue={setIncomeAmt} required={true} />
                    <FormSelect label="Are you currently paying any loans (hire purchase payments)?" name="debtStatus" id="debtStatus" content={optionsYN} value={debtStatus} setValue={setDebtStatus} required={true} />
                    {debtStatus === 'Yes' && (
                        <FormInput label="How much is your debt?" name="debtAmt" id="debtAmt" type="number" value={debtAmt} setValue={setDebtAmt} />
                    )}
                    <FormSelect label="Are your loan payments automated?" name="automatedPaymentsStatus" id="automatedPaymentsStatus" content={optionsYN} value={automatedPaymentsStatus} setValue={setAutomatedPaymentsStatus} required={true} />
                    <FormInput label="Your pension ID Number" name="pensionID" id="pensionID" type="text" value={pensionID} setValue={setPensionID} required={true} />
                    <FormInput label="Your current pension balance" name="pensionAsset" id="pensionAsset" type="number" value={pensionAsset} setValue={setPensionAsset} required={true} />
                    <FormSelect label="What is your pension contribution history (in years)" name="pensionContYrs" id="pensionContYrs" content={pensionContYrsOpts} value={pensionContYrs} setValue={setPensionContYrs} required={true} />
                    <FormSelect label="What is your pension contribution history (within 1 year)" name="pensionContOneYr" id="pensionContOneYr" content={pensionContOneYrOpts} value={pensionContOneYr} setValue={setPensionContOneYr} required={true} />
                    <FormSelect label="Average pension contribution per month" name="avgPensionCont" id="avgPensionCont" content={avgPensionContOpts} value={avgPensionCont} setValue={setAvgPensionCont} required={true} />
                    <FormSelect label="What type of pension assets are you using as collateral?" name="collateralType" id="collateralType" content={pensionCollateralOpts} value={collateralType} setValue={setCollateralType} required={true} />
                </SectionCard>

                {/* 2 — Scoring Information */}
                <SectionCard number="2" title="Scoring Information">
                    <FormInput label="Name of your employer (Company)" name="employerName" id="employerName" type="text" value={employerName} setValue={setEmployerName} required={true} />
                    <FormSelect label="Kindly state the number of years you worked with your current employer" name="yearsAtWork" id="yearsAtWork" content={numOfYearsWithEmployer} value={yearsAtWork} setValue={setYearsAtWork} required={true} />
                    <FormSelect label="Which of the following categories of sector does your employment fall under?" name="employmentSector" id="employmentSector" content={sectorOpts} value={employmentSector} setValue={setEmploymentSector} required={true} />
                </SectionCard>

                {/* 3 — Credit Behaviour */}
                <SectionCard number="3" title="Credit Behaviour Information">
                    <FormSelect label="Do you currently have any outstanding debts or loans?" name="outstandingLoans" id="outstandingLoans" content={optionsYN} value={outstandingLoans} setValue={setOutstandingLoans} required={true} />
                    <FormSelect label="Have you taken a loan from any financial institution?" name="loansTaken" id="loansTaken" content={optionsYN} value={loansTaken} setValue={setLoansTaken} required={true} />
                    <FormSelect label="What is the length of history of your credit/loan borrowing or payment history?" name="custRelTime" id="custRelTime" content={billsHistoryOpts} value={custRelTime} setValue={setCustRelTime} required={true} />
                    <FormSelect label="Have you ever defaulted on or missed a loan payment?" name="loanDefaultStatus" id="loanDefaultStatus" content={optionsYN} value={loanDefaultStatus} setValue={setLoanDefaultStatus} required={true} />
                    <FormSelect label="How many times did you miss payment on your loan?" name="defaultedLoanStatus" id="defaultedLoanStatus" content={missedPaymentOptions} value={defaultedLoanStatus} setValue={setDefaultedLoanStatus} required={true} />
                </SectionCard>

                {/* 4 — Bills Payment */}
                <SectionCard number="4" title="Bills Payment Information">
                    <FormSelect label="What are the bills that you are responsible for paying?" name="billsOption" id="billsOption" content={billPaymentOptions} value={billsOption} setValue={setBillsOption} required={true} />
                    <FormSelect label="What is the length of history of your bills payment history?" name="billsHistory" id="billsHistory" content={billsHistoryOpts} value={billsHistory} setValue={setBillsHistory} required={true} />
                    <FormSelect label="How many times did you miss payment on your bills?" name="billsHistoryStatus" id="billsHistoryStatus" content={missedPaymentOptions} value={billsHistoryStatus} setValue={setBillsHistoryStatus} required={true} />
                    <FormSelect label="Do you have 3 recent months loan payment history?" name="loanPaymentHistory" id="loanPaymentHistory" content={optionsYN} value={loanPaymentHistory} setValue={setLoanPaymentHistory} required={true} />
                    <FormSelect label="Do you have 3 recent months bills payment history?" name="billPaymentHistory" id="billPaymentHistory" content={optionsYN} value={billPaymentHistory} setValue={setBillPaymentHistory} required={true} />
                </SectionCard>

                {/* 5 — Savings Habit */}
                <SectionCard number="5" title="Savings Habit Information">
                    <div className="col-span-full">
                        <FormSelect label="Do you have a savings account held with a financial institution, pension or investment company that you can use for emergencies, such as sickness or job loss?" name="savingHabits" id="savingHabits" content={optionsYN} value={savingHabits} setValue={setSavingHabits} required={true} />
                    </div>
                    {savingHabits === 'Yes' && (
                        <>
                        <FormInput label="Month 1 savings" name="savingsFreq1" id="savingsFreq1" type="number" value={savingsFreq1} setValue={setSavingsFreq1} />
                        <FormInput label="Month 2 savings" name="savingsFreq2" id="savingsFreq2" type="number" value={savingsFreq2} setValue={setSavingsFreq2} />
                        <FormInput label="Month 3 savings" name="savingsFreq3" id="savingsFreq3" type="number" value={savingsFreq3} setValue={setSavingsFreq3} />
                        </>
                    )}
                    <FormSelect label="If you have appropriate insurance coverage, please indicate which of the following you have." name="typeOfInsurance" id="typeOfInsurance" content={insuranceOpts} value={typeOfInsurance} setValue={setTypeOfInsurance} required={true} />
                    <div className="col-span-full md:col-span-2">
                        <FormSelect label="Do you regularly save a minimum of 5% of your monthly income for emergency purposes or future financial goals?" name="eligibleSavingsHabit" id="eligibleSavingsHabit" content={optionsYN} value={eligibleSavingsHabit} setValue={setEligibleSavingsHabit} required={true} />
                    </div>
                </SectionCard>

                {/* 6 — Loan Information */}
                <SectionCard number="6" title="Loan Information">
                    <FormInput label="Loan amount" name="amountReq" id="amountReq" type="number" value={amountReq} setValue={setAmountReq} required={true} />
                    <FormSelect label="What is the purpose of the loan?" name="purpose" id="purpose" content={purposeOfLoanOpts} value={purpose} setValue={setPurpose} required={true} />
                    <FormSelect label="What is the desired payment plan?" name="paymentPlan" id="paymentPlan" content={paymentPlanOpts} value={paymentPlan} setValue={setPaymentPlan} required={true} />
                    <FormSelect label="What is the desired payment duration?" name="paymentDuration" id="paymentDuration" content={paymentDurationOpts} value={paymentDuration} setValue={setPaymentDuration} required={true} />
                    <FormInput label="Provide your source of income for loan repayment" name="repaySource" id="repaySource" type="text" value={repaySource} setValue={setRepaySource} required={true} />
                    <FormSelect label="What is the frequency of your income?" name="freqIncome" id="freqIncome" content={incomeFrequencyOpts} value={freqIncome} setValue={setFreqIncome} required={true} />
                    <FormSelect label="Please select your preferred automatic loan repayment method" name="autoDeduction" id="autoDeduction" content={automaticPaymentOpts} value={autoDeduction} setValue={setAutoDeduction} required={true} />
                    <div>
                        <label htmlFor="startDate" className="form-label">Select loan start date</label>
                        <input
                            type="date"
                            name="startDate"
                            id="startDate"
                            className="form-input disabled:opacity-75 disabled:bg-gray-200 placeholder:text-gray-700 disabled:cursor-not-allowed"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            max={nextMonthStartDate()}
                            required={true}
                        />
                    </div>
                    <FormSelect label="Is this loan a secure or unsecured loan?" name="pensionState" id="pensionState" content={pensionStateOpts} value={pensionState} setValue={setPensionState} required={true} />
                    <FormSelect label="Select the company with which you hold your tier-3 pension contribution" name="tier_3" id="tier_3" content={pensionCompanies} value={tier_3} setValue={setTier_3} required={true} />
                    <FormSelect label="Kindly select a relationship manager" name="relManager" id="relManager" content={relationshipManagers} value={relManager} setValue={setRelManager} required={true} />
                </SectionCard>

                {/* 7 — Guarantor Information */}
                <SectionCard number="7" title="Guarantor Information">
                    <FormInput label="Guarantor's first name" name="guarantorFirstName" id="guarantorFirstName" type="text" value={guarantorFirstName} setValue={setGuarantorFirstName} required={true} />
                    <FormInput label="Guarantor's last name" name="guarantorLastName" id="guarantorLastName" type="text" value={guarantorLastName} setValue={setGuarantorLastName} required={true} />
                    <FormInput label="Guarantor's phone number" name="guarantorPhone" id="guarantorPhone" type="text" value={guarantorPhone} setValue={setGuarantorPhone} required={true} />
                    <FormInput label="Guarantor's address" name="guarantorAddress" id="guarantorAddress" type="text" value={guarantorAddress} setValue={setGuarantorAddress} required={true} />
                    <FormInput label="Your relationship to guarantor" name="guarantorRelationship" id="guarantorRelationship" type="text" value={guarantorRelationship} setValue={setGuarantorRelationship} required={true} />
                    <FormInput label="Guarantor's GhanaCard number" name="guarantorIdNumber" id="guarantorIdNumber" type="text" value={guarantorIdNumber} setValue={setGuarantorIdNumber} required={true} />
                    <FormInput label="Guarantor's digital address" name="guarantorDigitalAddress" id="guarantorDigitalAddress" type="text" value={guarantorDigitalAddress} setValue={setGuarantorDigitalAddress} required={true} />
                    <div>
                        <label htmlFor="guarantorDob" className="form-label">Guarantor's date of birth</label>
                        <input
                            type="date"
                            name="guarantorDob"
                            id="guarantorDob"
                            className="form-input disabled:opacity-75 disabled:bg-gray-200 placeholder:text-gray-700 disabled:cursor-not-allowed"
                            value={guarantorDob}
                            onChange={e => setGuarantorDob(e.target.value)}
                            min="1930-01-01"
                            max={new Date().toISOString().split('T')[0]}
                            required={true}
                        />
                    </div>
                </SectionCard>

                {/* 8 — Files to Upload */}
                <SectionCard number="8" title="Files to Upload">
                    <FormUpload label="Proof of income" name="incomeProof" id="incomeProof" value={incomeProof} setValue={setIncomeProof} required={true} />
                    <FormUpload label="Proof of residence" name="residenceProof" id="residenceProof" value={residenceProof} setValue={setResidenceProof} required={true} />
                    <FormUpload label="Bank statement or MoMo statement" name="bankOrMomoStatement" id="bankOrMomoStatement" value={bankOrMomoStatement} setValue={setBankOrMomoStatement} required={true} />
                    <OtherDocsUpload setValue={setOtherDocs} />
                </SectionCard>

                {/* Authorization */}
                <div className="col-span-full rounded-xl border border-blue-200 dark:border-blue-500/20 bg-blue-50 dark:bg-blue-500/5 px-6 py-5 space-y-4">
                    <div>
                        <h4 className="text-sm font-semibold text-blue-800 dark:text-gray-300 mb-1.5">Loan Authorization</h4>
                        <p className="text-xs text-blue-700 dark:text-gray-300 leading-relaxed">
                            I, hereby apply for loans stated in Sections of this application. The information I have provided here is to the best of my knowledge true and correct. I authorize CedisPay Micro-Credit Enterprise to verify the correctness of this information and also to obtain additional information if it deems necessary in evaluating my loan application.
                        </p>
                    </div>
                    <div>
                        <h4 className="text-sm font-semibold text-blue-800 dark:text-gray-300 mb-1.5">Disclosure to Credit Reference Bureaus</h4>
                        <p className="text-xs text-blue-700 dark:text-gray-300 leading-relaxed">
                            CedisPay will obtain information about you from the credit reference bureaus to check your credit status and identity. The bureaus will record our enquiries which may be seen by other institutions that make their own credit enquiries about you. CedisPay shall also disclose your credit transactions to credit reference bureaus in accordance with the <strong>Credit Reporting Regulations, 2020 (L.I 2394)</strong>.
                        </p>
                    </div>
                    <FormInput label="Signed Name" name="signedName" id="signedName" type="text" value={signedName} disabled={true} />
                </div>

                {/* Submit */}
                <div className="col-span-full flex items-center gap-3 pt-2">
                    <Button
                        btnText="Check eligibility"
                        btnType="submit"
                        btnClasses="application-button w-max"
                        processing={processing}
                    />
                </div>

            </form>
        </div>

        {/* Eligibility modal */}
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={closeModal}>
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition.Child>
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
                                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-white/[.08]">
                                    <div className="flex items-center gap-2.5">
                                        <Dialog.Title className="text-sm font-semibold text-gray-900 dark:text-white">
                                            Pre-Check Status
                                        </Dialog.Title>
                                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${preCheckData?.status === 'accepted' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'}`}>
                                            {preCheckData?.status}
                                        </span>
                                    </div>
                                    <button type="button" onClick={closeModal} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                                        <HugeiconsIcon icon={Cancel01Icon} size={16} />
                                    </button>
                                </div>
                                <div className="px-5 pb-5">
                                    <EligCheck
                                        status={preCheckData?.status}
                                        data={preCheckData}
                                        loanType="pension"
                                        closeModal={closeModal}
                                        setReviewModalOpen={setReviewModalOpen}
                                    />
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>

        <ReviewModal isOpen={reviewModalOpen} isClose={setReviewModalOpen} client={signedName} />
        </>
    );
}

export default Pension;
